// StyleSheet for the shared Catalog list view + its mobile mount chrome.
// Lifted from apps/mobile's catalogStyles; takes a UiTheme so it renders
// identically on native and the web-rnw console. The list view consumes
// the search / section / row styles; the mobile mount reuses the
// header / back / title chrome (its native header stays in the mount, same
// split as the Maxes lift).
// RW-30 took the `editLink*` trio out with the "edit on web" doorway they
// dressed: it opened a WebView on a distribution that no longer exists.
import { StyleSheet } from "react-native";
import type { UiTheme } from "../theme";

export function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    safe: { flex: 1 },
    flex: { flex: 1 },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s3,
    },
    title: {
      color: p.ink,
      fontSize: t.sizes.xl,
      fontWeight: "600",
      letterSpacing: -0.4,
      flex: 1,
    },
    backChevron: {
      width: 40,
      height: 40,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: r.r2,
      marginLeft: -s.s2,
    },
    backChevronPressed: { backgroundColor: p.surface2 },
    backChevronText: {
      color: p.ink2,
      fontSize: t.sizes["2xl"],
      fontWeight: "700",
      lineHeight: t.sizes["2xl"],
    },
    searchWrap: {
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
    },
    search: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      color: p.ink,
      fontSize: t.sizes.base,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    clearFilters: {
      paddingHorizontal: s.s5,
      paddingVertical: s.s2,
      alignSelf: "flex-end",
    },
    clearFiltersPressed: { opacity: 0.7 },
    clearFiltersText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
    refreshingHint: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      paddingHorizontal: s.s5,
      paddingTop: s.s1,
      paddingBottom: s.s2,
    },
    refreshingHintText: {
      color: p.ink3,
      fontSize: t.sizes.xs,
    },
    sectionHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: p.surface2,
      paddingHorizontal: s.s5,
      paddingVertical: s.s3,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderColor: p.border,
    },
    sectionHeaderPressed: { opacity: 0.7 },
    sectionTitle: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    sectionRight: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
    },
    sectionCount: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontVariant: ["tabular-nums"],
    },
    sectionChevron: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      width: 12,
      textAlign: "center",
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
    },
    rowPressed: { backgroundColor: p.surface2 },
    // Split-mode (web master-detail) highlight for the selected row: tint +
    // bold name + a brand chevron. No side-stripe accent (banned); the state
    // reads by weight, not colour alone (a11y).
    rowSelected: { backgroundColor: p.surface2 },
    rowText: { flex: 1 },
    rowNameLine: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    rowName: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "500",
      flexShrink: 1,
    },
    rowNameSelected: { fontWeight: "700" },
    rowSub: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      marginTop: 2,
    },
    rowChevron: {
      color: p.ink3,
      fontSize: t.sizes.lg,
      fontWeight: "300",
      marginLeft: s.s3,
    },
    rowChevronSelected: { color: p.ink2, fontWeight: "600" }, // utility icon; selection = brand100 tint, not brand (S3.1)
    separator: {
      height: StyleSheet.hairlineWidth,
      marginLeft: s.s5,
    },
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: s.s5,
    },
    errorText: {
      color: p.danger,
      fontSize: t.sizes.sm,
    },
    emptyText: {
      color: p.ink3,
      fontSize: t.sizes.base,
    },
    // Compact filters (narrow web pane): a row of dropdown-style buttons +
    // an in-flow option checklist. Dense by default; no horizontal scroll,
    // no permanent chip wrap, no overflow-clipped popover.
    filterBar: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: s.s2,
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
    },
    filterBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      minHeight: 40,
      paddingHorizontal: s.s4,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
    },
    filterBtnActive: { borderColor: p.brand500 },
    filterBtnPressed: { backgroundColor: p.surface2 },
    filterBtnText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
    filterBtnTextActive: { color: p.brand500 },
    filterBtnCaret: { color: p.ink3, fontSize: t.sizes.xs },
    clearInline: {
      minHeight: 40,
      justifyContent: "center",
      paddingHorizontal: s.s3,
    },
    optionPanel: {
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
    },
    optionRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      minHeight: 44,
      paddingHorizontal: s.s3,
      borderRadius: r.r2,
    },
    optionRowPressed: { backgroundColor: p.surface2 },
    optionLabel: { color: p.ink2, fontSize: t.sizes.base },
    optionLabelOn: { color: p.ink, fontWeight: "700" },
    optionCheck: {
      color: p.brand500,
      fontSize: t.sizes.base,
      fontWeight: "700",
      width: 18,
      textAlign: "center",
    },
  });
}
